import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { 
  FileText, Users, Globe, CreditCard, Clock, 
  CheckCircle, AlertCircle, ArrowRight,
  TrendingUp, Activity, DollarSign, BarChart3, PieChart,
  Bell, Upload
} from 'lucide-react';
import { adminApi, notificationsApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [dashRes, notifRes] = await Promise.all([
          adminApi.getDashboard(),
          notificationsApi.getAll()
        ]);
        setDashboardData(dashRes.data);
        setNotifications(notifRes.data.notifications || []);
        setUnreadCount(notifRes.data.unread_count || 0);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const stats = dashboardData?.stats || {};
  const recentApplications = dashboardData?.recent_applications || [];
  const countryBreakdown = dashboardData?.country_breakdown || [];
  const monthlyTrends = dashboardData?.monthly_trends || [];

  const statCards = [
    { title: 'Total Applications', value: stats.total_applications || 0, icon: FileText, color: 'bg-blue-500' },
    { title: 'Pending Review', value: stats.pending_applications || 0, icon: Clock, color: 'bg-yellow-500' },
    { title: 'Approved', value: stats.approved_applications || 0, icon: CheckCircle, color: 'bg-green-500' },
    { title: 'Rejected', value: stats.rejected_applications || 0, icon: AlertCircle, color: 'bg-red-500' },
    { title: 'Total Revenue', value: `₹${(stats.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'bg-emerald-500', isText: true },
    { title: 'Total Users', value: stats.total_users || 0, icon: Users, color: 'bg-purple-500' },
    { title: 'Employees', value: stats.total_employees || 0, icon: Activity, color: 'bg-indigo-500' },
    { title: 'Countries', value: stats.total_countries || 0, icon: Globe, color: 'bg-teal-500' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'submitted':
      case 'under_review':
      case 'processing':
        return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Calculate max for bar chart scaling
  const maxCountryCount = Math.max(...countryBreakdown.map(c => c.count), 1);

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-[#4A5568] mt-1">Overview of your visa processing platform</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/countries/new">
              <Button variant="outline" className="border-[#D1CBBF]">
                <Globe className="w-4 h-4 mr-2" /> Add Country
              </Button>
            </Link>
            <Link to="/admin/visa-types/new">
              <Button className="bg-[#FF5A36] hover:bg-[#E0492B]">
                Add Visa Type
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-[#D1CBBF] animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-[#D1CBBF] card-hover" data-testid={`stat-card-${index}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#4A5568]">{stat.title}</p>
                    <p className={`${stat.isText ? 'text-xl' : 'text-2xl'} font-bold text-[#0A1E40]`}>{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Analytics Row */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Country Breakdown */}
            <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]" data-testid="country-analytics">
              <div className="flex items-center gap-2 mb-5">
                <BarChart3 className="w-5 h-5 text-[#FF5A36]" />
                <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Applications by Country
                </h2>
              </div>
              {countryBreakdown.length > 0 ? (
                <div className="space-y-3">
                  {countryBreakdown.slice(0, 8).map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-lg w-8"><CountryFlag code={item.code} size="md" /></span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[#0A1E40] truncate">{item.name}</span>
                          <span className="text-sm font-bold text-[#0A1E40] ml-2">{item.count}</span>
                        </div>
                        <div className="h-2 bg-[#F5F0E6] rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${(item.count / maxCountryCount) * 100}%`,
                              backgroundColor: ['#FF5A36', '#0A1E40', '#4A90D9', '#F5A623', '#7B68EE', '#50C878', '#FF6B6B', '#4ECDC4'][index % 8]
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#4A5568] text-center py-8">No application data yet</p>
              )}
            </div>

            {/* Application Status Distribution */}
            <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]" data-testid="status-analytics">
              <div className="flex items-center gap-2 mb-5">
                <PieChart className="w-5 h-5 text-[#FF5A36]" />
                <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Application Status Distribution
                </h2>
              </div>
              {(stats.total_applications || 0) > 0 ? (
                <div className="space-y-4">
                  {[
                    { label: 'Draft', count: stats.draft_applications || 0, color: 'bg-gray-400', textColor: 'text-gray-700' },
                    { label: 'Pending Review', count: stats.pending_applications || 0, color: 'bg-yellow-400', textColor: 'text-yellow-700' },
                    { label: 'Processing', count: stats.processing_applications || 0, color: 'bg-blue-400', textColor: 'text-blue-700' },
                    { label: 'Approved', count: stats.approved_applications || 0, color: 'bg-green-500', textColor: 'text-green-700' },
                    { label: 'Rejected', count: stats.rejected_applications || 0, color: 'bg-red-500', textColor: 'text-red-700' }
                  ].map((item, index) => {
                    const pct = stats.total_applications ? Math.round((item.count / stats.total_applications) * 100) : 0;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${item.color} shrink-0`}></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-[#0A1E40]">{item.label}</span>
                            <span className={`text-sm font-bold ${item.textColor}`}>{item.count} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-[#F5F0E6] rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${item.color} transition-all duration-500`}
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[#4A5568] text-center py-8">No application data yet</p>
              )}
            </div>

            {/* Monthly Trends */}
            {monthlyTrends.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-[#D1CBBF] lg:col-span-2" data-testid="monthly-analytics">
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp className="w-5 h-5 text-[#FF5A36]" />
                  <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Monthly Trends
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-3 min-w-[400px] h-48">
                    {monthlyTrends.slice(-12).map((item, index) => {
                      const maxApps = Math.max(...monthlyTrends.map(m => m.applications), 1);
                      const height = (item.applications / maxApps) * 100;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center gap-1 group" title={`${item.month}: ${item.applications} apps, ₹${item.revenue.toLocaleString()} revenue`}>
                          <span className="text-xs font-bold text-[#0A1E40] opacity-1 group-hover:opacity-100 transition-opacity">
                            {item.applications}
                          </span>
                          <div className="w-full flex flex-col gap-0.5" style={{ height: `${Math.max(height, 8)}%` }}>
                            <div className="flex-1 bg-[#FF5A36] rounded-t-md min-h-[4px]"></div>
                          </div>
                          <span className="text-xs text-[#4A5568] mt-1">
                            {item.month.split('-')[1]}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#D1CBBF]">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#FF5A36]"></div>
                    <span className="text-xs text-[#4A5568]">Applications</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notifications Panel */}
        {!loading && notifications.length > 0 && (
          <div className="bg-white rounded-xl border border-[#D1CBBF] mb-8 overflow-hidden" data-testid="dashboard-notifications">
            <div className="p-5 border-b border-[#D1CBBF] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#FF5A36]" />
                <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Recent Notifications
                </h2>
                {unreadCount > 0 && (
                  <span className="bg-[#FF5A36] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <Link to="/notifications">
                <Button variant="ghost" className="text-[#FF5A36] text-sm">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="divide-y divide-[#F5F0E6]">
              {notifications.slice(0, 5).map((notif) => (
                <Link
                  key={notif.notification_id}
                  to={`/admin/applications/${notif.application_id}`}
                  className={`flex items-center gap-4 px-5 py-4 hover:bg-[#F5F0E6]/50 transition-colors ${
                    !notif.is_read ? 'bg-[#FF5A36]/5' : ''
                  }`}
                  data-testid={`dash-notif-${notif.notification_id}`}
                >
                  {!notif.is_read && <div className="w-2 h-2 rounded-full bg-[#FF5A36] shrink-0" />}
                  <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                    <Upload className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${!notif.is_read ? 'text-[#0A1E40] font-bold' : 'text-[#4A5568] font-normal'}`}>
                      {notif.message}
                    </p>
                    <p className="text-xs text-[#9CA3AF]">{new Date(notif.created_at).toLocaleString()}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#D1CBBF] shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/admin/applications" className="block">
            <div className="bg-[#0A1E40] text-white rounded-xl p-6 card-hover">
              <FileText className="w-8 h-8 mb-3" />
              <h3 className="font-semibold text-lg mb-1">Manage Applications</h3>
              <p className="text-white/70 text-sm">Review and process visa applications</p>
            </div>
          </Link>
          <Link to="/admin/countries" className="block">
            <div className="bg-white border border-[#D1CBBF] rounded-xl p-6 card-hover">
              <Globe className="w-8 h-8 text-[#0A1E40] mb-3" />
              <h3 className="font-semibold text-lg text-[#0A1E40] mb-1">Manage Countries</h3>
              <p className="text-[#4A5568] text-sm">Add or edit destination countries</p>
            </div>
          </Link>
          <Link to="/admin/users" className="block">
            <div className="bg-white border border-[#D1CBBF] rounded-xl p-6 card-hover">
              <Users className="w-8 h-8 text-[#0A1E40] mb-3" />
              <h3 className="font-semibold text-lg text-[#0A1E40] mb-1">Manage Users</h3>
              <p className="text-[#4A5568] text-sm">View and manage user accounts</p>
            </div>
          </Link>
        </div>

        {/* Recent Applications */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Recent Applications
            </h2>
            <Link to="/admin/applications">
              <Button variant="ghost" className="text-[#FF5A36]">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-[#D1CBBF]">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              </div>
            </div>
          ) : recentApplications.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-[#D1CBBF]">
              <FileText className="w-12 h-12 text-[#D1CBBF] mx-auto mb-4" />
              <p className="text-[#4A5568]">No applications yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentApplications.map((app) => {
                const clientName = app.user?.name || app.travelers?.[0]?.first_name || 'Unknown';
                return (
                <div key={app.application_id} className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden hover:shadow-md transition-all" data-testid={`admin-app-row-${app.application_id}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-[#0A1E40] flex items-center justify-center shrink-0">
                          <span className="text-white font-bold text-sm">{clientName.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-[#0A1E40] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{clientName}</h3>
                          <div className="flex items-center gap-2 text-xs text-[#4A5568]">
                            <CountryFlag code={app.country?.code} size="xs" />
                            <span>{app.country?.name}</span>
                            <span className="text-[#D1CBBF]">·</span>
                            <span>{app.visa_type?.name || 'Visa'}</span>
                            {app.user?.email && (
                              <>
                                <span className="text-[#D1CBBF]">·</span>
                                <span>{app.user.email}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {app.submitted_by_partner ? (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-[#0A1E40] text-white">Partner</span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-[#F5F0E6] text-[#4A5568]">Candidate</span>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(app.status)}`}>
                          {app.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-[#F5F0E6] rounded-xl p-3">
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">App ID</p>
                        <Link to={`/admin/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-bold hover:underline">{app.application_id}</Link>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Source</p>
                        <p className="text-xs font-bold text-[#0A1E40]">{app.submitted_by_partner ? (app.partner_company || 'Partner') : 'Direct'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Fee</p>
                        <p className="text-xs font-bold text-[#0A1E40]">₹{app.total_fee?.toLocaleString() || '0'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Payment</p>
                        <p className={`text-xs font-bold ${app.payment_status === 'completed' ? 'text-green-600' : 'text-[#FF5A36]'}`}>{app.payment_status === 'completed' ? 'Paid' : 'Pending'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Assigned</p>
                        <p className="text-xs font-bold text-[#0A1E40]">{app.assigned_to || 'Unassigned'}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F5F0E6]">
                      <span className="text-xs text-[#4A5568]">Created: {new Date(app.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <Link to={`/admin/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-semibold hover:underline flex items-center gap-1">
                        View Details <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
